import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Flag, Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { FlagEditor } from "./FlagEditor";

const slug = (s) => s.replace(/\W+/g, "-").toLowerCase();

export const FlagsPanel = ({ flags, onChanged }) => {
  const [editing, setEditing] = useState(null);

  const toggle = async (flag, active) => {
    try {
      await api.put(`/admin/moderation/flags/${flag.id}`, { name: flag.name, category: flag.category, description: flag.description, active });
      toast.success(`"${flag.name}" ${active ? "enabled" : "disabled"}`);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update flag");
    }
  };

  const remove = async (flag) => {
    if (!window.confirm(`Delete the flag "${flag.name}"? Past violations keep their record.`)) return;
    try {
      await api.delete(`/admin/moderation/flags/${flag.id}`);
      toast.success("Flag deleted");
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete flag");
    }
  };

  const groups = (flags || []).reduce((acc, f) => ((acc[f.category] ||= []).push(f), acc), {});

  return (
    <div className="border border-border bg-[#0A0A0A] rounded-sm p-6 space-y-5" data-testid="flags-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Flag className="w-4 h-4 text-[#007AFF]" />
            <h2 className="font-heading text-lg font-black tracking-tight">Moderation flags</h2>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            The AI reads each outgoing message against every <span className="text-white font-semibold">active</span> flag's
            description. Write descriptions the way you'd brief a human moderator.
          </p>
        </div>
        <Button onClick={() => setEditing({})} data-testid="add-flag-btn"
          className="rounded-sm bg-[#007AFF] hover:bg-[#0066d6] text-white font-semibold gap-2 shrink-0">
          <Plus className="w-4 h-4" /> New flag
        </Button>
      </div>

      {flags === null && <p className="text-sm text-muted-foreground animate-pulse">Loading flags…</p>}
      {flags?.length === 0 && <p className="text-sm text-muted-foreground" data-testid="flags-empty">No flags yet — add one to start moderating.</p>}

      {Object.entries(groups).map(([category, list]) => (
        <div key={category} data-testid={`flag-group-${slug(category)}`}>
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">
            {category} <span className="text-white/40">({list.length})</span>
          </p>
          <div className="divide-y divide-border border border-border rounded-sm">
            {list.map((f) => (
              <div key={f.id} className={`flex items-start gap-4 px-4 py-3 ${f.active ? "" : "opacity-50"}`} data-testid={`flag-row-${f.id}`}>
                <Switch checked={f.active} onCheckedChange={(v) => toggle(f, v)} data-testid={`flag-toggle-${f.id}`} className="mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold" data-testid={`flag-name-${f.id}`}>{f.name}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">{f.description}</p>
                  <p className="text-[10px] font-mono text-muted-foreground mt-1">
                    {f.hit_count} hit{f.hit_count === 1 ? "" : "s"}{f.builtin ? " · built-in" : ""}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => setEditing(f)} data-testid={`edit-flag-btn-${f.id}`} title="Edit"
                    className="w-8 h-8 flex items-center justify-center border border-border text-muted-foreground hover:text-white hover:border-white rounded-sm transition-colors duration-200">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => remove(f)} data-testid={`delete-flag-btn-${f.id}`} title="Delete"
                    className="w-8 h-8 flex items-center justify-center border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-sm transition-colors duration-200">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {editing && (
        <FlagEditor flag={editing} categories={Object.keys(groups)} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChanged(); }} />
      )}
    </div>
  );
};
